import { useEffect, useState } from 'react'

import type { BufferEditor } from '~publish/legacy/editor/BufferEditor/types.plate'

import type {
  AutocompleteInputElement,
  AutocompleteInputElementInterface,
} from './nodes'
import type { AutocompleteItem, AutocompletePluginOptions } from './types'

const isAutocompletePlugin = (
  plugin: unknown,
): plugin is { options: AutocompletePluginOptions<AutocompleteInputElement> } =>
  typeof plugin === 'object' &&
  plugin !== null &&
  'options' in plugin &&
  typeof plugin.options === 'object' &&
  plugin.options !== null

/**
 * Fetches (debounced, per the owning plugin's fetchDebounceMs) and holds the
 * suggestion list for a mention's own render component. The query comes
 * from the combobox's own local search state, not the Slate document —
 * arrow/enter/escape navigation is InlineCombobox's own, native to its
 * input, and needs no forwarding here.
 */
export function useAutocompleteSuggestions<E extends AutocompleteInputElement>(
  editor: BufferEditor,
  elementInterface: AutocompleteInputElementInterface<E>,
  searchText: string,
  // Separate from `searchText` being empty: an empty search text is itself a
  // real, meaningful state a plugin's fetchNewSuggestions may want to handle
  // (e.g. returning recent items) — `enabled` is the actual "don't fetch at
  // all" switch, for a resolved mention that shouldn't keep re-fetching.
  enabled: boolean = true,
): {
  items: AutocompleteItem[]
  setItems: (items: AutocompleteItem[]) => void
  // Whatever a rejected fetchNewSuggestions call threw, for a consumer that
  // wants to render its own error state (e.g. a "failed to fetch" banner).
  error: unknown
  // True from the moment a query starts fetching until it settles, or until
  // a newer query supersedes it.
  isLoading: boolean
} {
  const [items, setItems] = useState<AutocompleteItem[]>([])
  const [error, setError] = useState<unknown>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    // Clear stale results from the previous query immediately, not just on
    // success: otherwise a fast Enter can select a suggestion that matched
    // an earlier, now-outdated search.
    setItems([])
    setError(null)

    if (!enabled) {
      setIsLoading(false)
      return
    }

    const plugin = editor.meta.pluginList.find(
      (candidate) => candidate.key === elementInterface.type,
    )
    const fetchNewSuggestions = isAutocompletePlugin(plugin)
      ? plugin.options.fetchNewSuggestions
      : undefined
    if (!fetchNewSuggestions) {
      setIsLoading(false)
      return
    }

    const debounceMs = isAutocompletePlugin(plugin)
      ? (plugin.options.fetchDebounceMs ?? 0)
      : 0

    let cancelled = false
    setIsLoading(true)
    const timeoutId = setTimeout(() => {
      fetchNewSuggestions(editor, searchText)
        .then((results: AutocompleteItem[]) => {
          if (!cancelled) {
            setItems(results)
            setIsLoading(false)
          }
          return undefined
        })
        .catch((fetchError: unknown) => {
          if (!cancelled) {
            setError(fetchError)
            setIsLoading(false)
          }
        })
    }, debounceMs)

    return (): void => {
      cancelled = true
      clearTimeout(timeoutId)
    }
  }, [searchText, editor, elementInterface, enabled])

  return { items, setItems, error, isLoading }
}
